/**
 * FirstStepsCard — "Fyrstu skrefin" checklist on the dashboard (owners).
 *
 * Replaces the idea of a product tour: five real tasks, each ticking itself
 * from real data, each linking straight to the place it happens. The card
 * removes itself the moment all five are done, or when dismissed.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight, ListChecks, X } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { usePlan } from '@/contexts/PlanContext';
import PaywallFlow from '@/components/PaywallFlow';
import PlanLimitUpsell from '@/components/PlanLimitUpsell';

const DISMISS_KEY = 'rektra.firstSteps.dismissed';
export const FINANCE_SEEN_KEY = 'rektra.firstSteps.projectFinanceSeen';

const read = (key) => {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
};

const FirstStepsCard = ({ counts = {}, teamMembers = 0, firstProjectId = null }) => {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const planCtx = usePlan();
  const is = lang === 'is';
  const [dismissed, setDismissed] = useState(() => read(DISMISS_KEY));
  const [capped, setCapped] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState(null);

  // Inviting is impossible on a full plan: sell the next plan instead of
  // sending the owner to a form that can only fail.
  const seatsFull = !!planCtx && planCtx.currentUsers >= planCtx.maxUsers;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  const steps = [
    {
      id: 'project',
      label: is ? 'Stofna verkefni' : 'Create a project',
      done: (counts.projects || 0) > 0,
      to: '/projects',
    },
    {
      id: 'hours',
      label: is ? 'Skrá tíma' : 'Log hours',
      done: (counts.time_entries || 0) > 0,
      to: '/time',
    },
    {
      id: 'receipt',
      label: is ? 'Skanna kvittun' : 'Scan a receipt',
      done: (counts.receipts || 0) > 0,
      to: '/receipts/new',
    },
    {
      id: 'invite',
      label: is ? 'Bjóða starfsmanni' : 'Invite an employee',
      done: teamMembers > 1,
      to: '/settings?tab=team',
      onClick: seatsFull ? () => setCapped(true) : null,
    },
    {
      id: 'finance',
      label: is ? 'Skoða fjármál verkefnis' : 'See a project\'s finances',
      done: read(FINANCE_SEEN_KEY),
      to: firstProjectId ? `/projects/${firstProjectId}?tab=finance` : '/projects',
    },
  ];

  const doneCount = steps.filter(s => s.done).length;
  if (dismissed || doneCount === steps.length) return null;

  return (
    <section
      className="rounded-xl border border-border bg-card p-4"
      data-testid="first-steps-card"
    >
      <div className="flex items-start gap-2.5 mb-3">
        <ListChecks size={16} className="text-accent mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold">{is ? 'Fyrstu skrefin' : 'First steps'}</h3>
          <p className="text-xs text-foreground-muted mt-0.5" data-testid="first-steps-progress">
            {doneCount}/{steps.length} {is ? 'búið' : 'done'}
          </p>
        </div>
        <button
          onClick={dismiss}
          className="p-1 text-foreground-muted hover:text-foreground rounded-md transition-colors flex-shrink-0"
          aria-label={is ? 'Loka' : 'Dismiss'}
          data-testid="first-steps-dismiss"
        >
          <X size={15} />
        </button>
      </div>

      <ul className="space-y-1">
        {steps.map((s) => (
          <li key={s.id}>
            <button
              onClick={() => (s.onClick ? s.onClick() : navigate(s.to))}
              className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-card-hover transition-colors text-left"
              data-testid={`first-steps-item-${s.id}`}
            >
              <span
                className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 ${
                  s.done ? 'bg-accent border-accent text-background' : 'border-border text-transparent'
                }`}
                data-testid={`first-steps-check-${s.id}`}
              >
                <Check size={12} strokeWidth={3} />
              </span>
              <span className={`text-sm flex-1 min-w-0 truncate ${s.done ? 'text-foreground-muted line-through' : 'text-foreground'}`}>
                {s.label}
              </span>
              {!s.done && <ChevronRight size={14} className="text-foreground-muted flex-shrink-0" />}
            </button>
          </li>
        ))}
      </ul>

      {capped && (
        <div className="mt-3">
          <PlanLimitUpsell
            plan={planCtx?.plan}
            maxUsers={planCtx?.maxUsers}
            lang={lang}
            onUpgrade={(next) => setUpgradePlan(next)}
            testId="first-steps-plan-limit"
          />
        </div>
      )}

      <PaywallFlow
        open={!!upgradePlan}
        initialPlan={upgradePlan}
        mode="full"
        onClose={() => setUpgradePlan(null)}
        onSuccess={async () => {
          setUpgradePlan(null);
          setCapped(false);
          await planCtx?.refresh?.();
        }}
      />
    </section>
  );
};

export default FirstStepsCard;
